import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../../components/Modal';
import { EstabelecimentosService } from '../system-admin/Service';
import { formatBRL } from '../../agenda/cliente/List/helpers';
import { urlFile } from '../../../AxiosConfig';
import { ToggleAddEstabUserBtn } from '../client/AddEstabUser';
import { useEstabsContext } from '../../../contexts/EstabsContext';
import { formatDuration } from '../../servicos/list/Duration';
import { MapPin, MessageCircle, TriangleAlert } from 'lucide-react';

const service = new EstabelecimentosService();

interface ServicosViewModalProps {
    estabId: string;
    onClose: () => void;
}

export const ViewEstabInfoModal = ({ estabId, onClose }: ServicosViewModalProps) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [estab, setEstab] = useState({} as any);
    const { estabsUser, selectClientEstab } = useEstabsContext();
    const linked = estabsUser.some((e) => e._id === estabId && e.type === 'client');

    const load = async () => {
        try {
            setLoading(true);
            setError(false);
            const { data } = await service.getInfoById(estabId);
            setEstab(data);
        } catch (err) {
            console.log(err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estabId]);

    const address = [
        estab?.address?.street,
        estab?.address?.number,
        estab?.address?.complement,
        estab?.address?.neighborhood,
        estab?.address?.city,
        estab?.address?.state,
    ]
        .filter(Boolean)
        .join(', ');
    const whatsappDigits = estab?.isPhoneNumberWhatsapp ? String(estab?.phoneNumber || '').replace(/\D/g, '') : '';
    const services: any[] = Array.isArray(estab?.servicos) ? estab.servicos : [];

    return (
        <Modal onClose={onClose} title={estab?.name || 'Estabelecimento'} desktopClassName="sm:!max-w-xl">
            {loading ? (
                <div className="space-y-3" aria-busy="true">
                    <div className="sf-skeleton h-24" />
                    <div className="sf-skeleton h-32" />
                </div>
            ) : error ? (
                <div className="sf-state">
                    <TriangleAlert aria-hidden />
                    <h2>Não foi possível carregar o estabelecimento</h2>
                    <button type="button" className="btn btn-primary btn-sm" onClick={load}>
                        Tentar novamente
                    </button>
                </div>
            ) : (
                <div className="space-y-5">
                    <div className="flex flex-col items-center gap-2 text-center">
                        {!!estab?.imageId && (
                            <img src={`${urlFile}/${estab.imageId}`} alt="" className="max-h-20 w-32 object-contain" />
                        )}
                        {!!estab?.description && (
                            <p className="text-sm text-base-content/70">{estab.description}</p>
                        )}
                        {!!address && <p className="text-xs text-base-content/55">{address}</p>}
                    </div>

                    {linked ? (
                        <button
                            type="button"
                            className="btn btn-primary w-full"
                            onClick={() => {
                                // O agendamento usa o estabelecimento selecionado no menu.
                                selectClientEstab(estabId);
                                navigate('/agenda?action=add');
                            }}
                        >
                            Agendar horário
                        </button>
                    ) : (
                        <ToggleAddEstabUserBtn estabId={estabId} estabName={estab?.name} />
                    )}

                    {(!!whatsappDigits || !!estab?.location) && (
                        <ul className="sf-group">
                            {!!whatsappDigits && (
                                <li>
                                    <a
                                        className="sf-row"
                                        href={`https://wa.me/${whatsappDigits}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <MessageCircle className="sf-row-check" aria-hidden />
                                        <span className="sf-row-body text-sm">Conversar no WhatsApp</span>
                                    </a>
                                </li>
                            )}
                            {!!estab?.location && (
                                <li>
                                    <a className="sf-row" href={estab.location} target="_blank" rel="noopener noreferrer">
                                        <MapPin className="sf-row-check" aria-hidden />
                                        <span className="sf-row-body text-sm">Ver no mapa</span>
                                    </a>
                                </li>
                            )}
                        </ul>
                    )}

                    {services.length > 0 && (
                        <section>
                            <h3 className="sf-group-title">Serviços</h3>
                            <ul className="sf-group">
                                {services.map((item, index) => (
                                    <li key={item._id || index}>
                                        <div className="sf-row">
                                            <span className="sf-row-body">
                                                <span className="sf-row-title">{item.name}</span>
                                                {!!item.description && <span className="sf-row-sub">{item.description}</span>}
                                                {!!item.duration && <span className="sf-row-sub">{formatDuration(item.duration)}</span>}
                                            </span>
                                            <span className="sf-row-end">{formatBRL(Number(item.price) || 0)}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {linked && (
                        <div className="sf-group">
                            <ToggleAddEstabUserBtn estabId={estabId} estabName={estab?.name} onDone={onClose} />
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
};
